import { Principal } from "@icp-sdk/core/principal"
import { CASES } from "./candid_cases.js"
import { createActor, canisterId } from "./declarations/backend"
import { queryReady } from "./query_ready.js"
import { loadIdentity } from "./identity.js"
import { Ed25519KeyIdentity } from "@icp-sdk/core/identity"

// Candid-aware equality for what a generated client returns: bigints, numbers
// (-0 is not 0), byte arrays, principals, arrays, and plain objects.
export const same = (a, b) => {
  if (typeof a === "number" && typeof b === "number") return Object.is(a, b)
  if (a instanceof Uint8Array && b instanceof Uint8Array) return a.length === b.length && a.every((x, i) => x === b[i])
  if (a instanceof Principal || b instanceof Principal) return a instanceof Principal && b instanceof Principal && a.toText() === b.toText()
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => same(x, b[i]))
  if (a && b && typeof a === "object" && typeof b === "object") {
    const ka = Object.keys(a)
    return ka.length === Object.keys(b).length && ka.every((k) => k in b && same(a[k], b[k]))
  }
  return a === b
}

// Runs one row against the actor and returns { ok, text }. `principals` is what
// a row's `expect` function gets (the text of each identity's principal).
export const runRow = async (row, actor, principals = {}) => {
  try {
    const reply = await actor[row.method](...row.args)
    if (row.fault || row.refusal !== undefined) return { ok: false, text: "replied, but a failure was expected" }
    if (row.check) return row.check(reply, principals) ? { ok: true, text: "✓" } : { ok: false, text: "✗ differs from expected" }
    const expected = typeof row.expect === "function" ? row.expect(principals) : row.expect
    return same(reply, expected) ? { ok: true, text: "✓" } : { ok: false, text: "✗ differs from expected" }
  } catch (err) {
    const message = String(err?.message ?? err)
    if (row.fault) {
      const ok = message.includes(`fault: ${row.fault}`)
      return { ok, text: ok ? "✓ faulted as expected" : "✗ faulted with a different message" }
    }
    if (row.refusal !== undefined) {
      const ok = message.includes(`refused: ${row.refusal}'`)
      return { ok, text: ok ? "✓ refused as expected" : "✗ refused with a different reason" }
    }
    return { ok: false, text: `Failed: ${message.split("\n")[0]}` }
  }
}

// Rows run in parallel, then any `later` rows (which depend on an earlier one).
// A script (`sequence`) runs strictly in order, one call after another.
export const runAll = async (rows, actor, onRow, sequence = false, principals = {}) => {
  // `actor` is one actor, or a function choosing one per row (by its `as`).
  const pick = typeof actor === "function" ? actor : () => actor
  const run = async (row) => onRow(row, await runRow(row, pick(row), principals))
  if (sequence) {
    for (const row of rows) await run(row)
    return
  }
  await Promise.all(rows.filter((r) => !r.later).map(run))
  await Promise.all(rows.filter((r) => r.later).map(run))
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".feature-cases").forEach((panel) => {
    const rows = CASES[panel.dataset.cases]
    if (!rows) return
    const body = panel.querySelector("tbody")
    const summary = panel.querySelector(".cases-summary")
    const cells = new Map()
    rows.forEach((row) => {
      const tr = body.insertRow()
      tr.insertCell().textContent = row.label
      tr.insertCell().textContent = row.shown
      cells.set(row, tr.insertCell())
    })

    panel.querySelector(".cases-run").addEventListener("click", async () => {
      if (!canisterId) { summary.textContent = "No backend canister id: deploy first."; return }
      if (canisterId === "dev-stub") { summary.textContent = "Needs a deployed canister (stubbed, no canister)."; return }
      // Three callers: anonymous, this browser's saved key, and a fresh stranger.
      const keys = { you: loadIdentity(), stranger: Ed25519KeyIdentity.generate() }
      const actors = {
        anonymous: await queryReady(createActor(canisterId)),
        you: await queryReady(createActor(canisterId, { agentOptions: { identity: keys.you } })),
        stranger: await queryReady(createActor(canisterId, { agentOptions: { identity: keys.stranger } })),
      }
      const principals = { now: Date.now(), anonymous: "2vxsx-fae", you: keys.you.getPrincipal().toText(), stranger: keys.stranger.getPrincipal().toText() }
      let passed = 0
      cells.forEach((cell) => { cell.textContent = "…" })
      await runAll(rows, (row) => actors[row.as ?? "anonymous"], (row, result) => {
        cells.get(row).textContent = result.text
        if (result.ok) passed += 1
        summary.textContent = `${passed} of ${rows.length} as expected`
      }, panel.dataset.sequence === "true", principals)
    })
  })
})
